import angular from 'angular';

/**
 * httpBuffer
 * Based on the HTTP Auth Interceptor Module for Angular by Witold Szczerba
 * https://github.com/witoldsz/angular-http-auth
 */

function buildUrl(url, serializedParams) {
  if (serializedParams.length > 0) {
    url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams;
  }
  return url;
}

function httpBufferService($injector, $q) {
  // Holds all the requests, so they can be re-requested in future.
  let buffer = [];

  // Service initialized later because of circular dependency problem.
  let $http;

  function retryHttpRequest(responseWrapper) {
    function successCallback(response) {
      responseWrapper.deferred.resolve(response);
    }

    function errorCallback(response) {
      responseWrapper.deferred.reject(response);
    }

    $http = $http || $injector.get('$http');

    // URL with params -- this is the http cache key
    const url = buildUrl(responseWrapper.config.url,
      responseWrapper.config.paramSerializer(responseWrapper.config.params));
    const httpCache = $injector.get('$cacheFactory').get('$http');

    // check to see if we have a promise (i.e. not an array) in the http cache.
    // if so, it'll resolve to a 401 token request, as we only ever retry the http call
    // if it first one failed with an authz 401 token request response. Since the
    // result promise is cached, it'll trigger the "retriedRequest" logout condition
    // as the objects are shared
    const cachedResult = httpCache.get(url);
    if (cachedResult && !angular.isArray(cachedResult)) {
      httpCache.remove(url);
    }
    $http(responseWrapper.config).then(successCallback, errorCallback);
  }

  return {
    /**
     * @name appendRequest
     * @description Appends HTTP request's configuration object to the buffer and
     * returns the promise of the deferred.
     * @param {object} requestConfig The config for the request not yet deployed
     *  to be queued to allow for the same request to be retried later
     * @return {promise} The promise to use to for the deferred request
     */
    appendRequest(requestConfig) {
      const deferred = $q.defer();

      this.append(requestConfig, deferred, 'request');
      return deferred.promise;
    },

    /**
     * @name appendResponse
     * @description Appends HTTP response's request configuration object with deferred
     *  response attached to buffer and returns the promise of the deferred.
     * @param {object} response The response for which the config for the request will
     *  be queued to allow for the same request to be retried later
     * @return {promise} The promise to use to for the retried request
     */
    appendResponse(response) {
      const deferred = $q.defer();

      this.append(response.config, deferred, 'response');
      return deferred.promise;
    },

    /**
     * @name append
     * @description Appends HTTP request configuration object with deferred response
     *  attached to buffer.
     * @param {object} config The config for the request to allow for the same request
     *  to be retried later
     * @param {promise} deferred The promise to use to for the retried request
     * @param {string} type The type of config - either request or response
     */
    append(config, deferred, type) {
      buffer.push({
        config,
        deferred,
        type
      });
    },

    /**
     * @name resolveAll
     * @description Resolves all the buffered configs then clears the buffer.
     */
    resolveAll() {
      buffer.forEach(value => {
        if (value.type === 'request') {
          value.deferred.resolve(value.config);
        } else {
          value.config.retriedRequest = true;
          retryHttpRequest(value);
        }
      });
      this.clear();
    },

    /**
     * @name clear
     * @description Clears the buffer.
     */
    clear() {
      buffer = [];
    },

    /**
     * @name size
     * @description returns the size of the buffer.
     * @return {Number} the size of the buffer
     */
    size() {
      return buffer.length;
    }
  };
}

httpBufferService.$inject = ['$injector', '$q'];

export default httpBufferService;
